import React from 'react';
import type { EllmoApiType } from './shared';

export type { EllmoApiType };

export interface EllmoApiSSRProps {
  /**
   * The pre-fetched data from the Ellmo API
   */
  data?: string | null;
  /**
   * Error from fetching data
   */
  error?: string | null;
  /**
   * Type of data to render
   */
  type?: EllmoApiType;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Whether to show errors
   */
  showErrors?: boolean;
  /**
   * Children render prop with data and error
   */
  children?: (data: string | null, error: string | null) => React.ReactNode;
}

/**
 * Ellmo API component for traditional SSR environments (Next.js Pages Router)
 * This component receives pre-fetched data and renders it.
 * Data fetching should be done in getServerSideProps or getStaticProps.
 * 
 * @example
 * ```tsx
 * // In your page file (pages/about.tsx)
 * import { EllmoApiSSR, fetchEllmoData } from 'ellmo-ai-react';
 * 
 * export async function getServerSideProps(context) {
 *   const { data, error } = await fetchEllmoData({
 *     clientId: 'your-client-id',
 *     url: context.resolvedUrl,
 *     type: 'semantic'
 *   });
 *   
 *   return {
 *     props: { ellmoData: data, ellmoError: error }
 *   };
 * }
 * 
 * export default function AboutPage({ ellmoData, ellmoError }) {
 *   return (
 *     <div>
 *       <h1>About Page</h1>
 *       <EllmoApiSSR data={ellmoData} error={ellmoError} />
 *     </div>
 *   );
 * }
 * ```
 */
const EllmoApiSSR: React.FC<EllmoApiSSRProps> = ({
  data = null,
  error = null,
  className = '',
  showErrors = false,
  children
}) => {
  // If children prop is provided, use render prop pattern
  if (children) {
    return <>{children(data, error)}</>;
  }

  // Show errors if enabled
  if (error && showErrors) {
    return (
      <div className={`ellmo-api-error ${className}`}>
        <div>Error: {error}</div>
      </div>
    );
  }

  // Show data if showErrors is enabled (for debugging)
  if (data && showErrors) {
    return (
      <div className={`ellmo-api-data ${className}`}>
        <pre>{data}</pre>
      </div>
    );
  }

  // Render HTML content if available
  if (data && !error) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: data }} />;
  }

  // Return null if no data
  return null;
};

export default EllmoApiSSR;
 
